import React, {useEffect, useState} from "react";
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Menu,
    MenuList,
    Paper,
    Typography
} from "@mui/material";
import FormFieldLayout from "./layout/FormFieldLayout";
import {
    AccountBalanceWallet,
    AccountTree,
    Assessment,
    AttachMoney,
    ContentCopy,
    Login,
    Logout,
    Paid,
    PrivacyTip,
    Settings,
    SyncAlt
} from "@mui/icons-material";
import {useDispatch, useSelector} from "react-redux";
import {fetchCurrentUser, fetchLogout, selectCurrentUser} from "../store/securitySlice";
import {useTranslation} from "react-i18next";
import {SUPPORTED_LOCALES, LOCALE_LABEL_KEY_BY_CODE} from "../constants/locales";

const NavigationMenu = ({open = false, onClose}) => {
    const {t, i18n} = useTranslation();
    const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
    const user = useSelector(state => selectCurrentUser(state));
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(fetchCurrentUser())
    }, []);

    const logOut = (event) => {
        event.stopPropagation();
        dispatch(fetchLogout());
        if (onClose) {
            onClose();
        }
    }

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    }

    const handleLanguageMenuOpen = (event) => {
        event.stopPropagation();
        setLanguageMenuAnchor(event.currentTarget);
    };

    const handleLanguageMenuClose = () => {
        setLanguageMenuAnchor(null);
    };

    const handleLanguageChange = (language) => {
        i18n.changeLanguage(language);
        setLanguageMenuAnchor(null);
    };

    const currentLangCode = (i18n.language || "en").trim().toLowerCase().split("-")[0];
    const currentLabelKey = LOCALE_LABEL_KEY_BY_CODE[currentLangCode];
    const currentLangShort = currentLabelKey ? t(currentLabelKey) : currentLangCode.toUpperCase();

    return (
        <>
            {open && <Box className="NavigationMenuOverlay" onClick={handleClose} />}
            <Paper className={`NavigationMenu${open ? " open" : ""}`}>
        <Box sx={{height: 100, bgcolor: "primary.main", position: "relative"}}>
            <FormFieldLayout>
                <Typography variant="h2" fontSize="xx-large"
                            sx={{color: "primary.contrastText", mb: 2}}>{t("app.name")}</Typography>
                <Box sx={{position: "absolute", bottom: 4, right: 8}}>
                    <Typography
                        component="span"
                        sx={{
                            color: "primary.contrastText",
                            fontSize: "0.8rem",
                            cursor: "pointer"
                        }}
                        onClick={handleLanguageMenuOpen}
                    >
                        {currentLangShort}
                    </Typography>
                </Box>
                <Menu
                    anchorEl={languageMenuAnchor}
                    open={Boolean(languageMenuAnchor)}
                    onClose={handleLanguageMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{ sx: { maxHeight: 400 } }}
                >
                    {SUPPORTED_LOCALES.map((code) => (
                        <MenuItem
                            key={code}
                            selected={(i18n.language || "").startsWith(code)}
                            onClick={() => handleLanguageChange(code)}
                        >
                            {code.toUpperCase()} — {t(LOCALE_LABEL_KEY_BY_CODE[code] || "language.english")}
                        </MenuItem>
                    ))}
                </Menu>
                {user ?
                    <IconButton onClick={logOut} sx={{position: "absolute", top: 0, right: 0, zIndex: 2000}}>
                        <Typography fontSize="medium">{t("navigation.logOut")}</Typography> <Logout fontSize="large"></Logout>
                    </IconButton> :
                    <IconButton onClick={() => {
                        navigate('/signin');
                        handleClose();
                    }}
                                sx={{position: "absolute", top: 0, right: 0, zIndex: 2000}}>
                        <Typography fontSize="medium">{t("navigation.logIn")}</Typography> <Login fontSize="large"></Login>
                    </IconButton>}
                {user && <Typography variant="body" sx={{color: "primary.contrastText"}}>{user.email}</Typography>}
            </FormFieldLayout>
        </Box>
        <MenuList style={{maxHeight: '80%', overflowY: "auto"}} onClick={handleClose}>
            <MenuItem component={RouterLink} to="/transactions">
                <ListItemIcon>
                    <SyncAlt fontSize="medium" color="primary"/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{variant: "h6"}}>{t("navigation.transactions")}</ListItemText>
            </MenuItem>
            <MenuItem component={RouterLink} to="/wallets">
                <ListItemIcon>
                    <AccountBalanceWallet fontSize="medium" color="primary"/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{variant: "h6"}}>{t("navigation.wallets")}</ListItemText>
            </MenuItem>
            <MenuItem component={RouterLink} to="/budgets">
                <ListItemIcon>
                    <AttachMoney fontSize="medium" color="primary"/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{variant: "h6"}}>{t("navigation.budgets")}</ListItemText>
            </MenuItem>
            <MenuItem component={RouterLink} to="/templates">
                <ListItemIcon>
                    <ContentCopy fontSize="medium" color="primary"/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{variant: "h6"}}>{t("navigation.templates")}</ListItemText>
            </MenuItem>
            <MenuItem component={RouterLink} to="/reports">
                <ListItemIcon>
                    <Assessment fontSize="medium" color="primary"/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{variant: "h6"}}>{t("navigation.reports")}</ListItemText>
            </MenuItem>
            <MenuItem component={RouterLink} to="/categories">
                <ListItemIcon>
                    <AccountTree fontSize="medium" color="primary"/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{variant: "h6"}}>{t("navigation.categories")}</ListItemText>
            </MenuItem>
            <MenuItem component={RouterLink} to="/currencies">
                <ListItemIcon>
                    <Paid fontSize="medium" color="primary"/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{variant: "h6"}}>{t("navigation.currencies")}</ListItemText>
            </MenuItem>
            <Divider/>
            <MenuItem component={RouterLink} to="/settings">
                <ListItemIcon>
                    <Settings fontSize="medium" color="primary"/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{variant: "h6"}}>{t("navigation.settings")}</ListItemText>
            </MenuItem>
            <MenuItem component={RouterLink} to="/privacy-policy">
                <ListItemIcon>
                    <PrivacyTip fontSize="medium" color="primary"/>
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{variant: "h6"}}>{t("navigation.privacyPolicy")}</ListItemText>
            </MenuItem>
            {/*<Divider/>*/}
            {/*<MenuItem>*/}
            {/*    <ListItemIcon>*/}
            {/*        <ContentCut fontSize="small"/>*/}
            {/*    </ListItemIcon>*/}
            {/*    <ListItemText>Example</ListItemText>*/}
            {/*    <Typography variant="body2" color="text.secondary">*/}
            {/*        ⌘X*/}
            {/*    </Typography>*/}
            {/*</MenuItem>*/}
        </MenuList>
    </Paper>
        </>
    );
}
export default NavigationMenu